import NoticeIcon from 'assets/icons/notice-yellow-icon.svg';
import InformPopup from 'components/common/InformPopup';
import {
  PAYTO_ERROR_CODE_MAPPING,
  PAYTO_ERROR_CODE_MAPPING_TITLE,
  PAYTO_LOG_TAG,
} from 'features/to/const';
import usePaytoAPI from 'features/to/hooks/payment/usePaytoAPI';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import { format } from 'helpers';
import useAddTransaction from 'hooks/form-add-transaction/useAddTransaction';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import usePage from 'hooks/usePage';
import usePortalModal from 'hooks/usePortalModal';
import { isFunction } from 'lodash';
import React from 'react';

const PaytoErrorCodePopup = ({
  isOpen,
  errorData = {},
  onClick,
  title,
  content,
  buttonLabel2 = '',
  modalOptions = {},
  modalData = {},
  fetchPaytoAccountsAgreementLatest,
}) => {
  const { t } = useLang();
  const { isDesktop } = useDetectDevice();
  const { closePortalModalName } = usePortalModal();
  const { isAddTransactionPage } = usePage();

  const {
    setPaymentMethodName: setFXPaymentMethodName,
    isMakePayment: isFxMakePayment,
    getTotalPayAmount,
  } = useFxFormAddTransactionStore();
  const { resetPaymentMethod, isMakePayment: isToMakePayment } =
    useFormAddTransactionStore();
  const { getTotalToPay } = useAddTransaction();
  const { resetPaytoAccountInformation, paytoAccountInformation } =
    usePaytoStore();
  const { payloadTransformFromPaytoAccountInformation } = usePaytoAPI();

  const { errorCode, debugMessage, message } = errorData || {};

  const { isHideTitle, failPath } = modalOptions || {};

  const { accountAgreement } = modalData || {};

  const totalToPay = isAddTransactionPage
    ? getTotalToPay()
    : getTotalPayAmount();

  const { payId, bankName, accountNumber, maxAmount } =
    accountAgreement ||
    payloadTransformFromPaytoAccountInformation(paytoAccountInformation) ||
    {};
  const isAgreementByBsb = !payId;

  const getPayToAccountString = () => {
    if (isAgreementByBsb) {
      if (!bankName || !accountNumber) return '';
      return `${bankName} ${accountNumber}`;
    }
    if (!payId) return '';
    return `PayID (${payId})`;
  };

  const paymentFailErrorCode = ['0200', '0201', '0202', '0203', '0204'];
  const accountInvalidErrorCode = ['0100', '0101', '0102'];
  const accountAmendInvalidErrorCode = ['0300'];
  const debugMessageErrorCode = ['0198', '0298', '0398'];
  const otpFailErrorCode = ['002'];
  const serverErrorCode = ['0299'];

  const errorCodeTransform = errorCode?.toUpperCase();

  const errorCodeNumber = errorCode?.slice(-4);
  const otpErrorCodeNumber = errorCode?.slice(-3);

  const getErrorTitleMessage = () => {
    if (isHideTitle) return '';
    if (PAYTO_ERROR_CODE_MAPPING_TITLE?.[errorCodeTransform])
      return t(PAYTO_ERROR_CODE_MAPPING_TITLE?.[errorCodeTransform], {
        account: getPayToAccountString(),
        maxAmount: format.toAmountFloatStr(maxAmount),
        totalToPay: format.toAmountFloatStr(totalToPay),
      });
    if (paymentFailErrorCode.includes(errorCodeNumber))
      return t('popup_payment_fail', {
        account: getPayToAccountString(),
        totalToPay: format.toAmountFloatStr(totalToPay),
      });
    if (accountInvalidErrorCode.includes(errorCodeNumber))
      return t('form_authorise_payto_account_rejected_title');
    if (accountAmendInvalidErrorCode.includes(errorCodeNumber))
      return t('form_authorise_payto_account_rejected_title');
    if (otpFailErrorCode.includes(errorCodeNumber))
      return t('popup_payto_otp_verification_failed_title');
    return title || t('label_important');
  };

  const getErrorContentMessage = () => {
    if (PAYTO_ERROR_CODE_MAPPING?.[errorCodeTransform])
      return t(PAYTO_ERROR_CODE_MAPPING?.[errorCodeTransform], {
        account: getPayToAccountString(),
        maxAmount: format.toAmountFloatStr(maxAmount),
        totalToPay: format.toAmountFloatStr(totalToPay),
      });
    if (errorCodeTransform?.includes('99')) return 'internal server error';
    if (debugMessageErrorCode.includes(errorCodeNumber))
      return debugMessage || message;
    return content || message || 'internal server error';
  };

  const getSubTextStyle = () => {
    if (serverErrorCode.includes(errorCodeNumber)) {
      return { textAlign: 'center', textAlignLast: 'unset' };
    }
    if (errorCodeTransform?.includes('99')) {
      return { textAlign: 'center', textAlignLast: 'unset' };
    }
    if (debugMessageErrorCode.includes(errorCodeNumber)) {
      return { textAlign: 'center', textAlignLast: 'unset' };
    }
    if (['0101', '0200', '0203', '6666'].includes(errorCodeNumber)) {
      return { textAlign: 'center', textAlignLast: 'unset' };
    }
    return {
      textAlign: 'justify',
      textAlignLast: 'left',
    };
  };

  const handleResetPaymentMethod = () => {
    if (paymentFailErrorCode[4] === errorCodeNumber) {
      if (isFxMakePayment) {
        setFXPaymentMethodName('');
      }
      if (isToMakePayment) {
        resetPaymentMethod();
      }
    }
  };

  const handleOnClick = () => {
    console.log(`⭐ ${PAYTO_LOG_TAG} 👉 close ➡️ errorData ↩`, errorData);
    if (paymentFailErrorCode.includes(errorCodeNumber)) {
      console.log(`⭐ ${PAYTO_LOG_TAG} 👉 close ➡️ paymentFailErrorCode ↩`);
      if (failPath) {
        fetchPaytoAccountsAgreementLatest(() => {
          isFunction(onClick) && onClick();
          // resetPaytoAccountInformation();
          handleResetPaymentMethod();
          closePortalModalName();
          window.location.href = failPath;
        }, false);
      } else {
        fetchPaytoAccountsAgreementLatest(() => {
          isFunction(onClick) && onClick();
          // resetPaytoAccountInformation();
          handleResetPaymentMethod();
          closePortalModalName();
        }, false);
      }
      return;
    }

    if (otpFailErrorCode.includes(otpErrorCodeNumber)) {
      console.log(`⭐ ${PAYTO_LOG_TAG} 👉 close ➡️ otpFailErrorCode ↩`);
      isFunction(onClick) && onClick();
      closePortalModalName();
      return;
    }

    if (serverErrorCode.includes(errorCodeNumber)) {
      console.log(`⭐ ${PAYTO_LOG_TAG} 👉 close ➡️ serverErrorCode ↩`);
      if (failPath) {
        fetchPaytoAccountsAgreementLatest(() => {
          isFunction(onClick) && onClick();
          // resetPaytoAccountInformation();
          handleResetPaymentMethod();
          closePortalModalName();
          window.location.href = failPath;
        }, false);
        return;
      }
    }

    console.log(`⭐ ${PAYTO_LOG_TAG} 👉 close ↩`);
    isFunction(onClick) && onClick();
  };

  return (
    <InformPopup
      isOpen={isOpen}
      imageSrc={NoticeIcon}
      imagesStyles={{
        width: isDesktop ? '75px' : '75px',
        height: isDesktop ? '60px' : '60px',
        marginBottom: '8px',
      }}
      text={getErrorTitleMessage()}
      content={getErrorContentMessage()}
      alignContent="center"
      buttonLabel2={buttonLabel2 || t('button_close')}
      onClick={handleOnClick}
      isBackdropOverlap={true}
      zIndexBackdropOverlap={1055}
      subTextStyle={getSubTextStyle()}
    />
  );
};

export default PaytoErrorCodePopup;
